import { Injectable } from '@angular/core';
import { HttpService } from 'app/services/http/http.service';
import { Attribute } from '@gainsystems/gains-ui-commons';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdjustmentResponse } from '../models/adjustment.model';
import { DemandData, DemandItem, DemandItemRecord, DemandType } from '../models/forecast-data.model';
import { SkuDetails } from '../models/sku-details.model';
import { SkuLocation } from '../models/sku-location.model';
import { secureRandom } from 'app/shared/util/secure-random.util';

@Injectable({
  providedIn: 'root'
})
export class ForecastService {
  constructor(private readonly _httpService: HttpService) {}
  getSkuLocations(isDemandPrediction = false): Observable<SkuLocation[]> {
    if (isDemandPrediction) {
      interface ApiSkuLocation {
        item_no: string;
        loc_no: string;
      }
      return this._httpService
        .createHttpClient()
        .url('/dp/api/v1/skuls')
        .get<ApiSkuLocation[]>()
        .pipe(
          map((apiData: ApiSkuLocation[]) =>
            apiData.map(
              (apiItem, index) =>
                ({
                  skulId: index + 1,
                  item: apiItem.item_no,
                  location: apiItem.loc_no,
                  department: 'N/A', // Default values since not provided by API
                  group: 'N/A',
                  description: `Item ${apiItem.item_no}`,
                  model: 'N/A',
                  modelName: 'N/A',
                  demandGrowth: 0,
                  confidenceLevel: 0
                }) as SkuLocation
            )
          )
        );
    } else {
      const departments = ['VR', 'PT', 'ANCHOR', 'ENG', 'MFG'];
      const groups = ['Pull Tube', 'Push Rod', 'Seal Cap', 'Piston Head'];
      const locations = ['14', '21', '33', '07', '85'];

      const skuLocations: SkuLocation[] = Array.from({ length: 27484 }, (_, index) => {
        const skulId = index + 1;
        return {
          skulId,
          item: skulId.toString().padStart(6, '0'), // <-- formatted item
          department: `${departments[skulId % departments.length]}&${departments[(skulId + 1) % departments.length]}`,
          location: locations[skulId % locations.length],
          group: groups[skulId % groups.length],
          description: `Detailed description for item ${skulId}`,
          model: `Model-${(skulId % 1000).toString().padStart(4, '0')}`,
          modelName: `ModelName-${skulId}`,
          demandGrowth: skulId % 101,
          confidenceLevel: 100 - (skulId % 100)
        };
      });
      return of(skuLocations);
    }
  }

  getSkuDetails(skulId: number): Observable<SkuDetails> {
    const departments = ['VR', 'PT', 'ANCHOR', 'ENG', 'MFG'];
    const groups = ['Pull Tube', 'Push Rod', 'Seal Cap', 'Piston Head'];
    const locations = ['14', '21', '33', '07', '85'];
    const skuDetails: SkuDetails = {
      skulId,
      item: skulId.toString().padStart(6, '0'), // <-- formatted item
      location: locations[skulId % locations.length],
      description: `Detailed description for item ${skulId}`,
      department: `${departments[skulId % departments.length]}&${departments[(skulId + 1) % departments.length]}`,
      group: groups[skulId % groups.length],
      model: `Model-${(skulId % 1000).toString().padStart(4, '0')}`,
      fmad: `FMAD-${(skulId * 7) % 100000}`,
      confidenceLevel: 100 - (skulId % 100),
      demandGrowth: skulId % 101
    };

    return of(skuDetails);
  }

  getAttributesDetails(skuDetails: SkuDetails): Observable<Attribute[][]> {
    return of([
      [
        { label: 'Item', value: skuDetails.item, id: 'item', readOnly: true, type: 'input' },
        { label: 'Location', value: skuDetails.location, id: 'location', readOnly: true, type: 'input' },
        {
          label: 'Stock Indicator',
          value: { title: 'A', value: 'A' },
          items: [
            { title: 'A', value: 'A' },
            { title: 'B', value: 'B' },
            { title: 'C', value: 'C' }
          ],
          id: 'stock-indicator',
          type: 'select'
        },
        { label: 'Lead Time', value: 20, id: 'lt', readOnly: true, type: 'input', dataType: 'number' },
        { label: 'Lead Time Override', value: -11, id: 'lto', type: 'input', dataType: 'number', numericMode: 'integer' },
        { label: 'Unshipped', value: 1, id: 'unshipped', readOnly: true, type: 'input', dataType: 'number', numericMode: 'integer' },
        { label: 'On Hand', value: 4, id: 'on-hand', readOnly: true, type: 'input', dataType: 'number', numericMode: 'integer' },
        { label: 'Cost', value: 2, id: 'cost', readOnly: true, type: 'input', dataType: 'number', numericMode: 'currency' }
      ],
      [
        {
          label: 'Approval Status',
          value: { title: 'A', value: 'A' },
          items: [
            { title: 'A', value: 'A' },
            { title: 'B', value: 'B' },
            { title: 'C', value: 'C' }
          ],
          id: 'approval-status',
          type: 'select'
        },
        { label: 'Next Forecast Review Date', value: new Date(), id: 'nfrd', type: 'date' },
        {
          label: 'Model',
          value: { title: skuDetails.model, value: skuDetails.model },
          id: 'model',
          type: 'select',
          items: [
            { title: skuDetails.model, value: skuDetails.model },
            { title: 'Model-002', value: 'B' },
            { title: 'Model-003', value: 'C' }
          ]
        },
        { label: 'Model Description', value: skuDetails.description, id: 'model-decription', readOnly: true, type: 'input' },
        { label: 'FMAD', value: skuDetails.fmad, id: 'fmad', readOnly: true, type: 'input' },
        { label: '$FMAD', value: 3, id: '$fmad', readOnly: true, type: 'input', dataType: 'number', numericMode: 'currency' },
        { label: 'Demand Percent', value: skuDetails.demandGrowth, id: 'demand-percent', readOnly: true, type: 'input', dataType: 'number', numericMode: 'percentage' },
        { label: 'Confidence Level', value: skuDetails.confidenceLevel, id: 'confidence-level', readOnly: true, type: 'input', dataType: 'number', numericMode: 'percentage' },
        {
          label: 'Seasonal',
          value: { title: 'Y', value: 'Y' },
          items: [
            { title: 'Y', value: 'Y' },
            { title: 'N', value: 'N' }
          ],
          id: 'seasonal',
          type: 'select'
        },
        { label: 'Annual Demand', value: 10, id: 'annual-demand', readOnly: true, type: 'input', dataType: 'number', numericMode: 'integer' },
        { label: 'MTD Sales', value: 10, id: 'mtd', readOnly: true, type: 'input', dataType: 'number', numericMode: 'integer' }
      ]
    ]);
  }
  getRandomInt(min: number, max: number): number {
    return Math.floor(secureRandom() * (max - min + 1)) + min;
  }
  generateRandomRecords(years: [number, number], periodNames: string[]): DemandItemRecord[] {
    const records: DemandItemRecord[] = [];

    periodNames.forEach((name, idx) => {
      const year = idx < 5 ? years[0] : years[1]; // simple split across 2 years
      records.push({
        rawValue: this.getRandomInt(1, 10),
        adjustment:
          secureRandom() > 0.7
            ? {
                value: this.getRandomInt(1, 10),
                notes: 'Random adjustment',
                createdAt: new Date(),
                createdBy: 'SYSTEM'
              }
            : null,
        period: {
          value: `${year}-${String(idx + 1).padStart(2, '0')}`,
          name,
          year: String(year)
        }
      });
    });

    return records;
  }

  getForecastData(): Observable<DemandData> {
    const periodNames = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];

    const items: DemandItem[] = [
      {
        type: DemandType.History,
        years: [2022, 2023],
        records: this.generateRandomRecords([2022, 2023], periodNames)
      },
      {
        type: DemandType.History,
        years: [2023, 2024],
        records: this.generateRandomRecords([2023, 2024], periodNames)
      },
      {
        type: DemandType.History,
        years: [2024, 2025],
        records: this.generateRandomRecords([2024, 2025], periodNames)
      },
      {
        type: DemandType.Forecast,
        years: [2025, 2026],
        records: this.generateRandomRecords([2025, 2026], periodNames)
      },
      {
        type: DemandType.AggHistory,
        years: [2024, 2025],
        records: this.generateRandomRecords([2024, 2025], periodNames)
      },
      {
        type: DemandType.AggHistory,
        years: [2022, 2023],
        records: this.generateRandomRecords([2022, 2023], periodNames)
      },
      {
        type: DemandType.TotDemand,
        years: [2025, 2026],
        records: this.generateRandomRecords([2025, 2026], periodNames)
      }
    ];

    return of({
      currentPlanPeriod: '2025-08',
      periodNames,
      items
    });
  }
  transformForecastData(data: DemandData): any {
    const months = data.periodNames ?? [];
    const getValue = (r?: DemandItemRecord): number | undefined => (r ? (r.adjustment?.value ?? r.rawValue) : undefined);
    const arrays: Record<string, (number | undefined)[]> = {};

    for (const item of data.items ?? []) {
      const key = item.type + (item.years && item.years.length === 2 ? item.years.map(y => String(y).slice(-2)).join('') : '');

      arrays[key] = months.map(m => getValue(item.records?.find(r => r.period?.name === m)));
    }

    const tableData = months.map((month, i) => {
      const row: Record<string, any> = { month };
      for (const [key, arr] of Object.entries(arrays)) {
        row[key] = arr[i];
      }
      return row;
    });

    const chartData = {
      months,
      ...arrays
    };

    return { tableData, chartData };
  }

  getForecastBase(): Observable<AdjustmentResponse> {
    const mockResponse: AdjustmentResponse = {
      rawValue: 10,
      adjustedValue: 12,
      period: {
        value: '2025-10',
        name: 'Oct',
        year: '2025'
      },
      notes: 'Test adjustment note',
      availablePeriods: [
        {
          value: '2025-09',
          name: 'Sep',
          year: '2025'
        },
        {
          value: '2025-10',
          name: 'Oct',
          year: '2025'
        },
        {
          value: '2025-11',
          name: 'Nov',
          year: '2025'
        },
        {
          value: '2025-12',
          name: 'Dec',
          year: '2025'
        },
        {
          value: '2026-01',
          name: 'Jan',
          year: '2026'
        },
        {
          value: '2026-02',
          name: 'Feb',
          year: '2026'
        },
        {
          value: '2026-03',
          name: 'Mar',
          year: '2026'
        },
        {
          value: '2026-04',
          name: 'Apr',
          year: '2026'
        },
        {
          value: '2026-05',
          name: 'May',
          year: '2026'
        },
        {
          value: '2026-06',
          name: 'Jun',
          year: '2026'
        },
        {
          value: '2026-07',
          name: 'Jul',
          year: '2026'
        },
        {
          value: '2026-08',
          name: 'Aug',
          year: '2026'
        }
      ]
    };
    return of(mockResponse);
  }
}
